import { useQuery } from '@apollo/client'
import {
    Box,
    Button,
    CircularProgress,
    makeStyles,
    Theme,
    Typography,
} from '@material-ui/core'
import { FilterList as FilterIcon } from '@material-ui/icons'
import React, { useEffect, useRef, useState } from 'react'
import { GET_BEST_PRACTICE_CLASHES } from '../../api/graphQL/queries/bestPracticeClash.queries'
import { BestPracticeClash } from '../../api/graphQL/types/bestPracticeClash.types'
import { ClashCard } from './ClashCard'

const useStyles = makeStyles((theme: Theme) => ({
    filterButton: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    content: {
        marginTop: theme.spacing(2),
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
}))

interface ClashListProps {
    projectId: string | null
    treePath?: string | null
    onClashResolved?: (clashId: string) => void
    skipQuery?: boolean
    fullWidth?: boolean
    className?: string
}

export const ClashList = ({
    projectId,
    treePath,
    onClashResolved,
    skipQuery = false,
    fullWidth = false,
    className,
}: ClashListProps) => {
    const classes = useStyles()
    const [showResolved, setShowResolved] = useState(false)
    const [clashes, setClashes] = useState<BestPracticeClash[]>([])
    const timeoutRef = useRef<NodeJS.Timeout>()

    const { data, loading, refetch } = useQuery(GET_BEST_PRACTICE_CLASHES, {
        variables: {
            input: {
                projectId: projectId,
                includeResolved: showResolved,
                treePath: treePath,
            },
        },
        skip: !projectId || skipQuery,
        fetchPolicy: 'network-only',
    })

    useEffect(() => {
        if (data?.bestPracticeClashes) {
            setClashes(data.bestPracticeClashes)
        }
    }, [data])

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    const handleToggleResolved = () => {
        setShowResolved(!showResolved)
    }

    const handleClashResolved = (clashId: string) => {
        setClashes((prevClashes) =>
            prevClashes.map((clash) =>
                clash.id === clashId
                    ? {
                          ...clash,
                          isResolved: true,
                          resolvedDate: new Date().toISOString(),
                      }
                    : clash
            )
        )

        // Notify parent
        onClashResolved?.(clashId)

        // Refetch to get updated data (with cleanup)
        timeoutRef.current = setTimeout(() => refetch(), 500)
    }

    const unresolvedCount = clashes.filter((c) => !c.isResolved).length

    return (
        <Box className={className}>
            <Button
                className={classes.filterButton}
                startIcon={<FilterIcon />}
                onClick={handleToggleResolved}
                size="small"
                variant="outlined"
                fullWidth={fullWidth}
            >
                {showResolved ? 'Hide Resolved' : 'Show Resolved'}
            </Button>

            <Box className={classes.content}>
                {loading ? (
                    <Box className={classes.loadingContainer}>
                        <CircularProgress />
                        <Typography variant="body2" style={{ marginTop: 16 }}>
                            &nbsp; Loading clashes...
                        </Typography>
                    </Box>
                ) : clashes.length === 0 ? (
                    <Box className={classes.emptyState}>
                        <Typography variant="body1" gutterBottom>
                            {showResolved
                                ? 'No clashes found'
                                : 'No unresolved clashes'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {showResolved
                                ? 'All Best Practices were applied successfully.'
                                : 'All clashes have been resolved.'}
                        </Typography>
                    </Box>
                ) : (
                    clashes.map((clash) => (
                        <ClashCard
                            key={clash.id}
                            clash={clash}
                            onResolved={handleClashResolved}
                        />
                    ))
                )}
            </Box>
        </Box>
    )
}

export const useClashCount = (clashes: BestPracticeClash[]): number => {
    return clashes.filter((c) => !c.isResolved).length
}
